import { Card, Button, Skeleton, Empty, message, Typography, Progress, Tag, Divider, Space, Tooltip, Checkbox } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, RocketOutlined, CrownOutlined, BankOutlined, WechatOutlined, AlipayOutlined, CreditCardOutlined, ReloadOutlined } from '@ant-design/icons';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/request';
import { useAuthStore, getFeatureFromQuota } from '../../stores/auth';
import { useRegionStore } from '../../stores/region';
import './Subscription.css';

const { Title, Text, Paragraph } = Typography;

interface Package {
  id: string;
  name: string;
  code: string;
  packageType: number;
  packageLevel: number;
  price: number;
  mapLimit: number;
  storageLimit: number;
  customModelLimit: number;
  apiEnabled: number;
  teamEnabled: number;
  features?: any;
  description?: string;
}

interface OrderInfo {
  orderNo: string;
  packageName: string;
  amount: number;
  packageLevel: number;
}

const PLAN_COLORS: Record<number, string> = {
  1: '#6b7280',
  2: '#3b82f6',
  3: '#8b5cf6',
  4: '#f59e0b',
};

const PLAN_ICONS: Record<number, React.ReactNode> = {
  1: <RocketOutlined />,
  2: <RocketOutlined />,
  3: <CrownOutlined />,
  4: <BankOutlined />,
};

const LEVEL_CODE_MAP: Record<string, number> = {
  free: 1, personal: 2, basic: 2, professional: 3, enterprise: 4,
};

function getLevelByCode(code: string): number {
  return LEVEL_CODE_MAP[code.toLowerCase()] ?? 1;
}

function formatStorage(bytes: number): string {
  if (!bytes) return '0';
  if (bytes >= 10737418240) return (bytes / 10737418240).toFixed(0) + ' GB';
  if (bytes >= 1048576) return (bytes / 1048576).toFixed(0) + ' MB';
  return (bytes / 1024).toFixed(0) + ' KB';
}

function formatStorageShort(bytes: number): string {
  if (!bytes) return '0 B';
  if (bytes >= 10737418240) return (bytes / 10737418240).toFixed(0) + ' GB';
  if (bytes >= 1048576) return (bytes / 1048576).toFixed(0) + ' MB';
  return (bytes / 1024).toFixed(0) + ' KB';
}

function getPaymentMethodsForRegion(reg: string): string[] {
  return reg === 'CN' ? ['wechat', 'alipay', 'bank'] : ['stripe', 'paypal'];
}

interface FeatureItem {
  key: string;
  labelKey: string;
  hintKey: string;
}

const FEATURE_GROUPS_CONFIG: { labelKey: string; features: FeatureItem[] }[] = [
  {
    labelKey: 'subscription.featureGroups.mapFeatures',
    features: [
      { key: 'maps', labelKey: 'subscription.features.maps', hintKey: 'subscription.features.mapsHint' },
      { key: 'basemap', labelKey: 'subscription.features.basemap', hintKey: 'subscription.features.basemapHint' },
      { key: 'export', labelKey: 'subscription.features.export', hintKey: 'subscription.features.exportHint' },
      { key: 'publish', labelKey: 'subscription.features.publish', hintKey: 'subscription.features.publishHint' },
    ],
  },
  {
    labelKey: 'subscription.featureGroups.advancedFeatures',
    features: [
      { key: 'models', labelKey: 'subscription.features.models', hintKey: 'subscription.features.modelsHint' },
      { key: 'api', labelKey: 'subscription.features.api', hintKey: 'subscription.features.apiHint' },
      { key: 'team', labelKey: 'subscription.features.team', hintKey: 'subscription.features.teamHint' },
    ],
  },
];

function parseFeatures(features: any): Record<string, boolean> {
  if (!features) return {};
  if (typeof features === 'string') {
    try { return JSON.parse(features); } catch { return {}; }
  }
  return features;
}

function getFeatureEnabled(pkg: Package, key: string): boolean {
  const feat = parseFeatures(pkg.features);
  if (key in feat) return !!feat[key];
  if (key === 'maps') return pkg.mapLimit !== 0;
  if (key === 'api') return pkg.apiEnabled === 1;
  if (key === 'team') return pkg.teamEnabled === 1;
  if (key === 'models') return pkg.customModelLimit > 0;
  return false;
}

export default function Subscription() {
  const { t } = useTranslation();
  const [packages, setPackages] = useState<Package[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [step, setStep] = useState<'list' | 'payment' | 'success'>('list');
  const [selectedPkg, setSelectedPkg] = useState<Package | null>(null);
  const [order, setOrder] = useState<OrderInfo | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<string>('wechat');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const { quota, subscription, updateQuota } = useAuthStore();
  const { region, regionInfo } = useRegionStore();

  useEffect(() => {
    const controller = new AbortController();
    api.get<any>('/subscription/current', { signal: controller.signal })
      .then(data => {
        if (data) {
          if (data.subscription !== undefined || data.packageLevel !== undefined) {
            useAuthStore.getState().updateSubscription(data);
          }
          if (data.quota) {
            updateQuota(data.quota);
          }
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, [updateQuota]);

  const currentLevel = subscription?.packageLevel
    ?? (quota?.planCode ? getLevelByCode(quota.planCode) : 1);

  const loadPackages = useCallback(async () => {
    setPackagesLoading(true);
    try {
      const data = await api.get<any[]>('/subscription/packages');
      setPackages(Array.isArray(data) ? data : []);
    } catch (err: any) {
      if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
        console.error('Failed to load packages:', err);
      }
    } finally {
      setPackagesLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    api.get<any[]>('/subscription/packages', { signal: controller.signal })
      .then((data) => setPackages(Array.isArray(data) ? data : []))
      .catch((err: any) => {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
          console.error('Failed to load packages:', err);
        }
      })
      .finally(() => setPackagesLoading(false));
    return () => controller.abort();
  }, []);

  // Sync default payment method when region changes
  useEffect(() => {
    const defaultPayment = region === 'CN' ? 'wechat' : 'stripe';
    setSelectedPayment(defaultPayment);
  }, [region]);

  const mapPct = quota && quota.totalMaps > 0 ? Math.min(100, (quota.usedMaps / quota.totalMaps) * 100) : 0;
  const storagePct = quota && quota.totalStorage > 0 ? Math.min(100, (quota.usedStorage / quota.totalStorage) * 100) : 0;

  const handleUpgrade = (pkg: Package) => {
    setSelectedPkg(pkg);
    setStep('payment');
  };

  const handleCreateAndPay = async () => {
    if (!selectedPkg) return;
    if (!agreeTerms) {
      message.warning(t('subscription.payment.termsRequired'));
      return;
    }
    setActionLoading(true);
    try {
      const result = await api.post<any>('/subscription/subscribe', {
        packageId: selectedPkg.id,
      });
      if (result.orderNo) {
        setOrder(result);
        const payRes = await api.post<any>('/subscription/subscribe/pay', {
          orderNo: result.orderNo,
          paymentMethod: selectedPayment,
        });
        if (payRes.orderNo) {
          message.success(t('subscription.paySuccess'));
          const freshQuota = await api.get<any>('/subscription/current');
          if (freshQuota.quota) updateQuota(freshQuota.quota);
          setStep('success');
        }
      }
    } catch (err: any) {
      message.error(err?.response?.data?.message || t('subscription.operationFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleBackToList = () => {
    setStep('list');
    setSelectedPkg(null);
    setOrder(null);
    setAgreeTerms(false);
    setSelectedPayment('wechat');
  };

  const getPaymentMethodLabel = (key: string) => {
    switch (key) {
      case 'wechat': return t('subscription.payment.wechatPay');
      case 'alipay': return t('subscription.payment.alipay');
      case 'bank': return t('subscription.payment.bankCard');
      case 'stripe': return t('subscription.payment.stripe');
      case 'paypal': return t('subscription.payment.paypal');
      default: return key;
    }
  };

  const getPaymentMethodColor = (key: string) => {
    switch (key) {
      case 'wechat': return '#07c160';
      case 'alipay': return '#1677ff';
      case 'bank': return '#6b7280';
      case 'stripe': return '#635bff';
      case 'paypal': return '#003087';
      default: return '#6b7280';
    }
  };

  const getPaymentMethodIcon = (key: string) => {
    switch (key) {
      case 'wechat': return <WechatOutlined />;
      case 'alipay': return <AlipayOutlined />;
      case 'bank': return <CreditCardOutlined />;
      case 'stripe': return <CreditCardOutlined />;
      case 'paypal': return <CreditCardOutlined />;
      default: return <CreditCardOutlined />;
    }
  };

  const renderTermsCheckbox = () => (
    <span
      style={{ fontSize: 13 }}
      dangerouslySetInnerHTML={{
        __html: t('subscription.payment.termsAgree')
          .replace(/<terms>(.*?)<\/terms>/, '<span style="color:#0969da">$1</span>')
          .replace(/<privacy>(.*?)<\/privacy>/, '<span style="color:#0969da">$1</span>'),
      }}
    />
  );

  if (packagesLoading) {
    return (
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px' }}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  // ================== Step: Payment ==================
  if (step === 'payment' && selectedPkg) {
    return (
      <div className="subscription-page">
        <div className="payment-header">
          <Title level={3} style={{ margin: 0 }}>{t('subscription.payment.title')}</Title>
          <Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 14 }}>
            <span
              style={{ color: '#0969da', cursor: 'pointer' }}
              onClick={handleBackToList}
            >
              ← {t('subscription.payment.backToPlans')}
            </span>
          </Paragraph>
        </div>

        <div className="payment-layout">
          <Card className="payment-package-card">
            <div className="payment-package-header">
              <div
                className="payment-package-icon"
                style={{ background: `${PLAN_COLORS[selectedPkg.packageLevel]}15`, color: PLAN_COLORS[selectedPkg.packageLevel] }}
              >
                {PLAN_ICONS[selectedPkg.packageLevel]}
              </div>
              <div>
                <div className="payment-package-name">{selectedPkg.name}</div>
                <Text type="secondary" style={{ fontSize: 13 }}>{t('subscription.payment.yearlySubscription')}</Text>
              </div>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <div className="confirm-detail">
              <div className="confirm-row">
                <span>{t('subscription.payment.packageName')}</span>
                <span>{selectedPkg.name}</span>
              </div>
              <div className="confirm-row">
                <span>{t('subscription.payment.subscriptionCycle')}</span>
                <span>{t('subscription.payment.oneYear')}</span>
              </div>
              <div className="confirm-row">
                <span>{t('subscription.mapsQuota')}</span>
                <span>{selectedPkg.mapLimit === -1 ? t('subscription.unlimited') : `${selectedPkg.mapLimit} ${t('subscription.unlimited')}`}</span>
              </div>
              <div className="confirm-row">
                <span>{t('subscription.storage')}</span>
                <span>{formatStorage(selectedPkg.storageLimit)}</span>
              </div>
              <div className="confirm-row confirm-row--highlight">
                <span>{t('subscription.payment.amountDue')}</span>
                <span className="confirm-price">{regionInfo?.currencySymbol ?? '¥'}{selectedPkg.price}</span>
              </div>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <div className="payment-features">
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('subscription.payment.openFeatures')}</div>
              {FEATURE_GROUPS_CONFIG.flatMap(g => g.features).filter(f => getFeatureEnabled(selectedPkg, f.key)).map(f => (
                <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 13 }}>
                  <CheckCircleFilled style={{ color: '#22c55e', fontSize: 13 }} />
                  <span>{t(f.labelKey)}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="payment-methods-card">
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: '#374151' }}>{t('subscription.payment.paymentMethods')}</div>

            <div className="payment-methods-grid">
              {getPaymentMethodsForRegion(region).map((key) => {
                const color = getPaymentMethodColor(key);
                const isSelected = selectedPayment === key;
                return (
                  <div
                    key={key}
                    className={`payment-method-item ${isSelected ? 'payment-method-item--selected' : ''}`}
                    onClick={() => setSelectedPayment(key)}
                    style={
                      isSelected
                        ? { borderColor: color, background: `${color}08` }
                        : {}
                    }
                  >
                    {isSelected && (
                      <CheckCircleFilled style={{ position: 'absolute', top: 8, right: 8, color, fontSize: 14 }} />
                    )}
                    <div style={{ color, fontSize: 26 }}>{getPaymentMethodIcon(key)}</div>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{getPaymentMethodLabel(key)}</span>
                  </div>
                );
              })}
            </div>

            <Divider style={{ margin: '24px 0 16px' }} />

            <div className="payment-summary">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Text type="secondary">{t('subscription.payment.amountDue')}</Text>
                <div className="confirm-price" style={{ fontSize: 28 }}>{regionInfo?.currencySymbol ?? '¥'}{selectedPkg.price}</div>
              </div>

              <Checkbox
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                style={{ marginBottom: 16 }}
              >
                {renderTermsCheckbox()}
              </Checkbox>

              <Button
                block
                type="primary"
                size="large"
                loading={actionLoading}
                onClick={handleCreateAndPay}
                disabled={!agreeTerms}
                style={{
                  background: agreeTerms ? '#f59e0b' : '#d1d5db',
                  borderColor: agreeTerms ? '#f59e0b' : '#d1d5db',
                  height: 48,
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                {actionLoading ? t('subscription.payment.process') : t('subscription.payment.payNow').replace('{price}', (regionInfo?.currencySymbol ?? '¥') + selectedPkg.price)}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // ================== Step: Success ==================
  if (step === 'success' && order) {
    return (
      <div className="subscription-page">
        <Card className="success-card">
          <div className="success-icon">
            <CheckCircleFilled style={{ color: '#22c55e', fontSize: 64 }} />
          </div>
          <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>{t('subscription.success.title')}</Title>
          <Paragraph type="secondary" style={{ marginBottom: 24 }}>
            {t('subscription.success.subtitle').replace('{plan}', order.packageName)}
          </Paragraph>

          <div className="success-order-info">
            <div className="confirm-row">
              <span>{t('subscription.success.orderNo')}</span>
              <span style={{ fontFamily: 'monospace' }}>{order.orderNo}</span>
            </div>
            <div className="confirm-row">
              <span>{t('subscription.success.paymentMethod')}</span>
              <span>{getPaymentMethodLabel(selectedPayment)}</span>
            </div>
            <div className="confirm-row">
              <span>{t('subscription.success.paymentAmount')}</span>
              <span className="confirm-price">{regionInfo?.currencySymbol ?? '¥'}{order.amount}</span>
            </div>
          </div>

          <Space style={{ width: '100%', marginTop: 8 }} size={12}>
            <Button block size="large" icon={<ReloadOutlined />} onClick={handleBackToList}>
              {t('subscription.success.backToPlans')}
            </Button>
            <Button block type="primary" size="large" onClick={() => window.location.href = '/maps'}>
              {t('subscription.success.startUsing')}
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  // ================== Step: List ==================
  return (
    <div className="subscription-page">
      <div className="page-header">
        <Title level={3} style={{ margin: 0 }}>{t('subscription.title')}</Title>
        <Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 14 }}>
          {t('subscription.subtitle')}
        </Paragraph>
      </div>

      {/* Current Usage Card */}
      {quota && (
        <Card className="usage-card" styles={{ body: { padding: '24px 28px' } }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 40 }}>
            <div className="quota-metric">
              <div className="metric-label">{t('subscription.mapsQuota')}</div>
              <div className="metric-value">
                <span className="used">{quota.usedMaps}</span>
                <span className="sep">/</span>
                <span className="total">{quota.totalMaps === -1 ? '∞' : quota.totalMaps}</span>
              </div>
              <Progress
                percent={mapPct}
                showInfo={false}
                strokeColor={PLAN_COLORS[currentLevel]}
                trailColor="#f0f0f0"
                size="small"
                style={{ marginTop: 8 }}
              />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {quota.totalMaps === -1 ? t('subscription.unlimitedCount') : t('subscription.mapsRemaining').replace('{count}', String(quota.remainingMaps))}
          </Text>
            </div>
            <div className="quota-metric">
              <div className="metric-label">{t('subscription.storage')}</div>
              <div className="metric-value">
                <span className="used">{formatStorageShort(quota.usedStorage)}</span>
                <span className="sep">/</span>
                <span className="total">{formatStorageShort(quota.totalStorage)}</span>
              </div>
              <Progress
                percent={storagePct}
                showInfo={false}
                strokeColor={PLAN_COLORS[currentLevel]}
                trailColor="#f0f0f0"
                size="small"
                style={{ marginTop: 8 }}
              />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('subscription.storageAvailable').replace('{amount}', formatStorageShort(quota.remainingStorage))}
          </Text>
            </div>
            <div className="quota-metric">
              <div className="metric-label">{t('subscription.enabledFeatures')}</div>
              <div className="feature-tags">
                {FEATURE_GROUPS_CONFIG.flatMap(g => g.features).filter(f => getFeatureFromQuota(quota?.permissions, f.key)).map(f => (
                  <Tag key={f.key} style={{ borderRadius: 4, fontSize: 12, marginBottom: 4 }}>{t(f.labelKey)}</Tag>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Pricing Plans */}
      {packages.length === 0 ? (
        <Card><Empty description={t('subscription.noPackages')}><Button type="primary" onClick={loadPackages}>{t('subscription.refresh')}</Button></Empty></Card>
      ) : (
        <div className="plans-grid">
          {packages.map((pkg) => {
            const isCurrent = pkg.packageLevel === currentLevel;
            const isUpgrade = pkg.packageLevel > currentLevel;
            const color = PLAN_COLORS[pkg.packageLevel] || '#3b82f6';

            return (
              <Card
                key={pkg.id}
                className={`plan-card ${pkg.packageLevel === 3 ? 'plan-card--popular' : ''} ${isCurrent ? 'plan-card--current' : ''}`}
                styles={{ body: { padding: 0, overflow: 'hidden' } }}
              >
                {pkg.packageLevel === 3 && (
                  <div className="popular-label">
                    <span style={{ fontSize: 11 }}>{t('subscription.recommended')}</span>
                  </div>
                )}

                <div className="plan-header" style={{ borderBottom: `1px solid ${color}22` }}>
                  <div className="plan-icon" style={{ background: `${color}15`, color }}>
                    {PLAN_ICONS[pkg.packageLevel]}
                  </div>
                  <div className="plan-name">{pkg.name}</div>
                  <div className="plan-price">
                    {pkg.price === 0 ? (
                      <span className="price-free">{t('subscription.free')}</span>
                    ) : (
                      <>
                        <span className="price-symbol">{regionInfo?.currencySymbol ?? '¥'}</span>
                        <span className="price-amount">{pkg.price}</span>
                        <span className="price-unit">{t('subscription.perYear')}</span>
                      </>
                    )}
                  </div>
                  {pkg.price === 0 ? (
                    <>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {pkg.mapLimit === -1 ? t('subscription.unlimitedMaps') : t('subscription.mapsIncluded').replace('{count}', String(pkg.mapLimit))}
                      </Text>
                      {pkg.storageLimit && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                          {t('subscription.storage') + ': ' + formatStorageShort(pkg.storageLimit)}
                        </Text>
                      )}
                    </>
                  ) : (
                    <>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {pkg.mapLimit === -1 ? t('subscription.unlimitedMaps') : t('subscription.mapsIncluded').replace('{count}', String(pkg.mapLimit))}
                      </Text>
                      {pkg.storageLimit && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {pkg.storageLimit === -1 ? t('subscription.storage') + ': ' + t('subscription.unlimited') : t('subscription.storage') + ': ' + formatStorageShort(pkg.storageLimit)}
                        </Text>
                      )}
                    </>
                  )}
                </div>

                <div className="plan-features">
                  {FEATURE_GROUPS_CONFIG.map((group) => (
                    <div key={group.labelKey} className="feature-group">
                      <div className="feature-group-label">{t(group.labelKey)}</div>
                      {group.features.map((feat) => {
                        const enabled = getFeatureEnabled(pkg, feat.key);
                        return (
                          <Tooltip key={feat.key} title={t(feat.hintKey)} placement="right">
                            <div className={`feature-item ${enabled ? 'feature-item--on' : 'feature-item--off'}`}>
                              {enabled
                                ? <CheckCircleFilled style={{ color: '#22c55e', fontSize: 14 }} />
                                : <CloseCircleFilled style={{ color: '#d1d5db', fontSize: 14 }} />
                              }
                              <span>{t(feat.labelKey)}</span>
                            </div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  ))}
                </div>

                <div className="plan-action">
                  {isCurrent ? (
                    <Button
                      block
                      size="large"
                      disabled
                      style={{
                        background: color,
                        borderColor: color,
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {t('subscription.currentPlan')}
                    </Button>
                  ) : pkg.price === 0 ? (
                    <Button block size="large" disabled type="default" style={{ fontSize: 14 }}>
                      {t('subscription.unavailable')}
                    </Button>
                  ) : isUpgrade ? (
                    <Button
                      block
                      size="large"
                      type="primary"
                      style={{ background: color, borderColor: color, fontSize: 14 }}
                      onClick={() => handleUpgrade(pkg)}
                    >
                      {t('subscription.upgradeNow')}
                    </Button>
                  ) : (
                    <Button block size="large" type="default" disabled style={{ fontSize: 14 }}>
                      {t('subscription.downgradeNotSupported')}
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}

          {/* Enterprise Self-Deployment */}
          <Card
            className="plan-card plan-card--enterprise"
            styles={{ body: { padding: 0, overflow: 'hidden' } }}
          >
            <div className="enterprise-label">{t('subscription.custom')}</div>
            <div className="plan-header" style={{ borderBottom: '1px solid #f59e0b22' }}>
              <div className="plan-icon" style={{ background: '#f59e0b15', color: '#f59e0b' }}>
                <BankOutlined />
              </div>
              <div className="plan-name">{t('subscription.enterprisePlan')}</div>
              <div className="plan-price">
                <span className="price-custom">{t('subscription.priceOnDemand')}</span>
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('subscription.enterpriseDesc')}
              </Text>
            </div>

            <div className="plan-features">
              {[
                t('subscription.enterpriseFeatures.privateDeploy'),
                t('subscription.enterpriseFeatures.customDev'),
                t('subscription.enterpriseFeatures.techSupport'),
                t('subscription.enterpriseFeatures.unlimitedMaps'),
                t('subscription.enterpriseFeatures.dedicatedDb'),
                t('subscription.enterpriseFeatures.chinaEnv'),
              ].map((feat, i) => (
                <div key={i} className="feature-item feature-item--on">
                  <CheckCircleFilled style={{ color: '#22c55e', fontSize: 14 }} />
                  <span>{feat}</span>
                </div>
              ))}
            </div>

            <div className="plan-action">
              <Button
                block
                size="large"
                style={{ background: '#f59e0b', borderColor: '#f59e0b', color: '#fff', fontWeight: 600, fontSize: 14 }}
                onClick={() => window.open('mailto:kimap.founder@gmail.com?subject=Enterprise Deployment Inquiry', '_blank')}
              >
                {t('subscription.contactUs')}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
